import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SignedIn, SignedOut, UserButton } from '@clerk/clerk-react';
import styles from './Landing.module.css';
import {
  PhysicsIcon,
  ChemistryIcon,
  BiologyIcon,
  MathematicsIcon,
} from '../components/icons';

// Research node data for the visualization
const RESEARCH_NODES = [
  { id: 1, x: 15, y: 10, type: 'experiment', label: 'F = ma' },
  { id: 2, x: 45, y: 5, type: 'data', label: 'Δt' },
  { id: 3, x: 75, y: 12, type: 'equation', label: '∫' },
  { id: 4, x: 10, y: 40, type: 'experiment', label: 'PV=nRT' },
  { id: 5, x: 40, y: 35, type: 'data', label: 'λ' },
  { id: 6, x: 70, y: 42, type: 'equation', label: '∂/∂x' },
  { id: 7, x: 20, y: 65, type: 'experiment', label: 'E=mc²' },
  { id: 8, x: 50, y: 60, type: 'data', label: 'σ' },
  { id: 9, x: 80, y: 68, type: 'equation', label: '∑' },
  { id: 10, x: 12, y: 88, type: 'experiment', label: 'k' },
  { id: 11, x: 55, y: 85, type: 'data', label: 'α' },
  { id: 12, x: 85, y: 92, type: 'equation', label: 'lim' },
];

const CONNECTIONS = [
  [1, 2], [2, 3], [1, 4], [4, 5], [5, 6],
  [4, 7], [7, 8], [8, 9], [7, 10], [10, 11],
  [11, 12], [2, 5], [5, 8], [8, 11], [3, 6],
  [6, 9], [9, 12]
];

function ResearchNodeGraph() {
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState<number | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const maxOffset = 10;
      const x = ((e.clientX - centerX) / centerX) * maxOffset;
      const y = ((e.clientY - centerY) / centerY) * maxOffset;
      setParallax({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const getNodeOpacity = (nodeId: number) => {
    if (hoveredNode === null) return 1;
    const isConnected = CONNECTIONS.some(([a, b]) =>
      (a === nodeId && b === hoveredNode) || (b === nodeId && a === hoveredNode)
    );
    return nodeId === hoveredNode ? 1 : isConnected ? 0.7 : 0.3;
  };

  return (
    <svg
      className={styles.researchGraph}
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      style={{
        transform: `translate(${parallax.x}px, ${parallax.y}px)`,
      }}
    >
      {/* Background grid pattern */}
      <defs>
        <pattern id="smallGrid" width="5" height="5" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.3" fill="#1a1a1a" />
        </pattern>
      </defs>
      <rect width="100" height="100" fill="url(#smallGrid)" opacity="0.5" />

      {/* Connection lines */}
      {CONNECTIONS.map(([from, to], i) => {
        const fromNode = RESEARCH_NODES.find(n => n.id === from);
        const toNode = RESEARCH_NODES.find(n => n.id === to);
        if (!fromNode || !toNode) return null;
        const opacity = (hoveredNode === null ||
          from === hoveredNode || to === hoveredNode) ? 0.3 : 0.1;
        return (
          <line
            key={`line-${i}`}
            x1={fromNode.x}
            y1={fromNode.y}
            x2={toNode.x}
            y2={toNode.y}
            stroke="#333"
            strokeWidth="0.3"
            strokeDasharray="1,1"
            className={styles.connectionLine}
            style={{ opacity }}
          />
        );
      })}

      {/* Nodes */}
      {RESEARCH_NODES.map((node) => (
        <g
          key={node.id}
          className={styles.nodeGroup}
          style={{ opacity: getNodeOpacity(node.id) }}
          onMouseEnter={() => setHoveredNode(node.id)}
          onMouseLeave={() => setHoveredNode(null)}
        >
          <circle
            cx={node.x}
            cy={node.y}
            r={node.type === 'experiment' ? 4 : node.type === 'data' ? 3 : 3.5}
            fill={node.type === 'experiment' ? '#2a4a3a' : node.type === 'data' ? '#3a3a5a' : '#4a3a3a'}
            stroke={node.type === 'experiment' ? '#3a6a5a' : node.type === 'data' ? '#5a5a7a' : '#6a5a5a'}
            strokeWidth="0.5"
            className={styles.node}
          />
          <text
            x={node.x}
            y={node.y + 1}
            textAnchor="middle"
            dominantBaseline="middle"
            className={styles.nodeLabel}
            style={{
              fontSize: node.type === 'experiment' ? '3' : '2.5',
              fill: '#888'
            }}
          >
            {node.label}
          </text>
        </g>
      ))}

      {/* Glow effect behind graph */}
      <circle
        cx="50"
        cy="50"
        r="35"
        fill="url(#glowGradient)"
        opacity="0.1"
      />
      <defs>
        <radialGradient id="glowGradient" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#2a4a3a" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
    </svg>
  );
}

export function Landing() {
  return (
    <div className={styles.landing}>
      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          <div className={styles.logo}>
            <Link to="/" className={styles.logoLink}>
              <img src="/logo.png" alt="AxLabs" className={styles.logoImage} />
            </Link>
          </div>
          <div className={styles.navLinks}>
            <Link to="/experiments" className={styles.navLink}>Labs</Link>
            <Link to="/subjects" className={styles.navLink}>Subjects</Link>
            <Link to="/curriculum" className={styles.navLink}>Curriculum</Link>
            <Link to="/tools" className={styles.navLink}>Tools</Link>
            <Link to="/research" className={styles.navLink}>Research</Link>
            <Link to="/about" className={styles.navLink}>About</Link>
          </div>
          <div className={styles.navSecondary}>
            <Link to="/documentation" className={styles.navSecondaryLink}>Documentation</Link>
            <a href="https://github.com/staticpayload/axlabs" className={styles.navSecondaryLink} target="_blank" rel="noopener noreferrer">GitHub</a>
            <SignedOut>
              <Link to="/sign-in" className={styles.navSecondaryLink}>Sign In</Link>
              <Link to="/sign-up" className={styles.navButton}>Sign Up</Link>
            </SignedOut>
            <SignedIn>
              <UserButton afterSignOutUrl="/"/>
            </SignedIn>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroBackground}>
          <div className={styles.heroGrid} />
        </div>

        <div className={styles.heroContainer}>
          {/* Left Column - Content */}
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>
              <span className={styles.heroTitleLine}>Research-Grade</span>
              <span className={styles.heroTitleLine}>Virtual Laboratories</span>
            </h1>

            <p className={styles.heroSubtitle}>
              An open infrastructure for physics, chemistry, biology, and mathematics education.
              Built for schools and universities that value correctness over convenience.
            </p>

            <p className={styles.heroScope}>
              Covering school and college-level curricula in Physics, Chemistry, Biology, Mathematics
            </p>

            <div className={styles.heroMetadata}>
              <span className={styles.metadataItem}>A subsidiary of OmenSystems.com</span>
              <span className={styles.metadataItem}>Built & maintained by staticpayload</span>
              <span className={styles.metadataItem}>Research-first. Not exam-first.</span>
            </div>

            <div className={styles.heroActions}>
              <Link to="/experiments" className={styles.primaryButton}>Explore Labs</Link>
              <Link to="/curriculum" className={styles.secondaryButton}>View Curriculum</Link>
              <Link to="/philosophy" className={styles.textLink}>Read Philosophy →</Link>
            </div>
          </div>

          {/* Right Column - Visual System */}
          <div className={styles.heroRight}>
            <div className={styles.visualSystem}>
              <ResearchNodeGraph />
            </div>
          </div>
        </div>
      </section>

      {/* What Is AxLabs */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionNumber}>01.</h2>
            <h2 className={styles.sectionTitle}>What Is AxLabs</h2>
          </div>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              AxLabs is a collection of deterministic science simulations. Each experiment runs real governing equations
              with SI units, numerical stability checks, and reproducible outputs. There is no AI, no gamification,
              no shortcuts. Students learn by observing consequences of their inputs.
            </p>
          </div>
        </div>
      </section>

      {/* How It's Different */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionNumber}>02.</h2>
            <h2 className={styles.sectionTitle}>How It's Different</h2>
          </div>
          <div className={styles.differentGrid}>
            <div className={styles.differentItem}>
              <h3 className={styles.differentTitle}>Real equations, not animations</h3>
              <p className={styles.differentText}>
                Every simulation solves actual differential equations. No pre-baked outcomes, no interpolation between keyframes.
              </p>
            </div>
            <div className={styles.differentItem}>
              <h3 className={styles.differentTitle}>Failure modes, not hand-holding</h3>
              <p className={styles.differentText}>
                Experiments can fail. Measurements can be invalid. Students encounter real consequences, not sanitized success paths.
              </p>
            </div>
            <div className={styles.differentItem}>
              <h3 className={styles.differentTitle}>Reproducible, not approximate</h3>
              <p className={styles.differentText}>
                Identical parameters yield identical results. Enable fair grading and legitimate scientific inquiry.
              </p>
            </div>
            <div className={styles.differentItem}>
              <h3 className={styles.differentTitle}>Gradeable, not subjective</h3>
              <p className={styles.differentText}>
                Deterministic outputs allow objective assessment. Every student receives unique parameters with equivalent difficulty.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionNumber}>03.</h2>
            <h2 className={styles.sectionTitle}>Who It's For</h2>
          </div>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              High school and university educators who need a reliable, auditable laboratory platform.
              Institutions that cannot afford incorrect physics. Students who want to understand, not just pass.
            </p>
          </div>
        </div>
      </section>

      {/* Subjects */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionNumber}>04.</h2>
            <h2 className={styles.sectionTitle}>Subjects</h2>
          </div>
          <div className={styles.subjectsGrid}>
            <Link to="/subjects/physics" className={styles.subjectCard}>
              <div className={styles.subjectIcon}>
                <PhysicsIcon size={32} />
              </div>
              <h3 className={styles.subjectName}>Physics</h3>
              <p className={styles.subjectDesc}>
                Classical mechanics, electromagnetism, thermodynamics, and wave phenomena.
                Real differential equations, not approximations.
              </p>
            </Link>
            <Link to="/subjects/chemistry" className={styles.subjectCard}>
              <div className={styles.subjectIcon}>
                <ChemistryIcon size={32} />
              </div>
              <h3 className={styles.subjectName}>Chemistry</h3>
              <p className={styles.subjectDesc}>
                Reaction kinetics, equilibrium, stoichiometry, and molecular dynamics.
                Deterministic simulation of chemical processes.
              </p>
            </Link>
            <Link to="/subjects/biology" className={styles.subjectCard}>
              <div className={styles.subjectIcon}>
                <BiologyIcon size={32} />
              </div>
              <h3 className={styles.subjectName}>Biology</h3>
              <p className={styles.subjectDesc}>
                Population dynamics, enzyme kinetics, diffusion processes, and ecological systems.
                Model-driven biological simulations.
              </p>
            </Link>
            <Link to="/subjects/mathematics" className={styles.subjectCard}>
              <div className={styles.subjectIcon}>
                <MathematicsIcon size={32} />
              </div>
              <h3 className={styles.subjectName}>Mathematics</h3>
              <p className={styles.subjectDesc}>
                Calculus, differential equations, numerical analysis, and optimization.
                Visualize mathematical concepts through interaction.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* How The Labs Work */}
      <section className={styles.section}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionNumber}>05.</h2>
            <h2 className={styles.sectionTitle}>How The Labs Work</h2>
          </div>
          <div className={styles.workflow}>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>1</span>
              <p className={styles.workflowText}>Select an experiment</p>
            </div>
            <span className={styles.workflowArrow}>&rarr;</span>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>2</span>
              <p className={styles.workflowText}>Configure parameters</p>
            </div>
            <span className={styles.workflowArrow}>&rarr;</span>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>3</span>
              <p className={styles.workflowText}>Run the simulation</p>
            </div>
            <span className={styles.workflowArrow}>&rarr;</span>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>4</span>
              <p className={styles.workflowText}>Observe results</p>
            </div>
            <span className={styles.workflowArrow}>&rarr;</span>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>5</span>
              <p className={styles.workflowText}>Compare to theory</p>
            </div>
            <span className={styles.workflowArrow}>&rarr;</span>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>6</span>
              <p className={styles.workflowText}>Repeat with variations</p>
            </div>
            <span className={styles.workflowArrow}>&rarr;</span>
            <div className={styles.workflowStep}>
              <span className={styles.workflowNumber}>7</span>
              <p className={styles.workflowText}>Export data for analysis</p>
            </div>
          </div>
        </div>
      </section>

      {/* Long-Term Vision */}
      <section className={styles.sectionAlt}>
        <div className={styles.sectionContainer}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionNumber}>06.</h2>
            <h2 className={styles.sectionTitle}>Long-Term Vision</h2>
          </div>
          <div className={styles.prose}>
            <p className={styles.proseText}>
              AxLabs is infrastructure, not a product. It is designed to be maintained for decades, not pivoted in quarters.
              Every decision prioritizes correctness and longevity over growth metrics.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerMain}>
            <div className={styles.footerIdentity}>
              <img src="/logo.png" alt="AxLabs" className={styles.footerLogoImage} />
              <span className={styles.footerTagline}>Deterministic Science Simulation Infrastructure</span>
            </div>
            <div className={styles.footerLinks}>
              <a href="https://github.com/staticpayload/axlabs" className={styles.footerLink} target="_blank" rel="noopener noreferrer">
                github.com/staticpayload/axlabs
              </a>
            </div>
          </div>
          <div className={styles.footerMeta}>
            <span className={styles.footerLine}>A subsidiary of OmenSystems.com</span>
            <span className={styles.footerLine}>Built & maintained by staticpayload</span>
            <span className={styles.footerCopyright}>&copy; 2024 AxLabs. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
